import { useLocation, Navigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { LandingPageHero } from '@/components/landing/LandingPageHero';
import { LandingPageContentSection } from '@/components/landing/LandingPageContentSection';
import { LandingPageFAQ } from '@/components/landing/LandingPageFAQ';
import { LandingPageCTA } from '@/components/landing/LandingPageCTA';
import { LandingPageBreadcrumb } from '@/components/landing/LandingPageBreadcrumb';
import {
  getBreadcrumbJsonLd,
  getFaqJsonLd,
  BASE_URL,
} from '@/lib/seo-constants';
import { LANDING_CONFIGS, type LandingConfig } from '@/lib/landingConfigs';

/**
 * Generic renderer for all geo & category landing pages. The concrete config
 * is looked up from the pathname so we can register 14 routes without 14
 * near-identical page components.
 */
export default function GeoLanding() {
  const { pathname } = useLocation();
  const key = normalizeKey(pathname);
  const config = LANDING_CONFIGS[key];

  if (!config) return <Navigate to="/" replace />;

  return <GeoLandingView config={config} />;
}

/** Strip trailing slash and localize by mapping /en/... to the same key. */
function normalizeKey(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/g, '');
  return trimmed;
}

function GeoLandingView({ config }: { config: LandingConfig }) {
  const noIndexOtherLang = config.lang === 'de' ? { en: true } : { de: true };
  const seoPath = config.slug.startsWith('/en/') ? config.slug.slice(3) : config.slug;
  const canonicalUrl = config.lang === 'en'
    ? `${BASE_URL}${config.slug}`
    : `${BASE_URL}${config.slug}`;

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    provider: { '@id': `${BASE_URL}/#organization` },
    name: config.serviceName,
    description: config.serviceDescription,
    url: canonicalUrl,
    areaServed: config.areaServed.map((a) => ({ '@type': 'Place', name: a })),
    inLanguage: config.lang,
  };

  const jsonLd = [
    getBreadcrumbJsonLd(
      [{ name: config.breadcrumb, path: seoPath }],
      config.lang,
    ),
    serviceJsonLd,
    getFaqJsonLd(config.faqs, config.lang),
  ];

  return (
    <Layout>
      <SEOHead
        title={config.seoTitle}
        description={config.seoDescription}
        path={seoPath}
        jsonLd={jsonLd}
        noIndex={noIndexOtherLang}
      />
      <LandingPageBreadcrumb current={config.breadcrumb} />
      <LandingPageHero
        badge={config.badge}
        title={config.h1}
        highlight={config.h1Highlight}
        description={config.heroDescription}
        ctaLabel={config.ctaLabel}
        ctaHref="/kontakt"
      />
      <LandingPageContentSection
        heading={config.contentHeading}
        intro={config.contentIntro}
        blocks={config.blocks}
      />
      <LandingPageFAQ faqs={config.faqs} />
      <LandingPageCTA
        heading={config.ctaHeading}
        description={config.ctaDescription}
      />
    </Layout>
  );
}